import { useEffect, useState } from 'react';
import axios from 'axios';
import { SpinnerWheel } from '@/components/SpinnerWheel';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Coffee, Utensils, ChefHat, MapPin, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIONS = [
  { id: 'Maharashtra', tagline: 'Bold spices, misal magic', accent: '#F97316' },
  { id: 'Goa', tagline: 'Coastal, coconut, sol kadhi', accent: '#2D4A22' },
  { id: 'Kerala', tagline: 'Backwaters, appam, curry leaves', accent: '#15803D' },
];

const COMING_SOON = ['Punjab', 'Gujarat', 'Tamil Nadu', 'Bengal', 'Rajasthan'];

const MEALS = [
  { value: 'breakfast', label: 'Breakfast', icon: Coffee },
  { value: 'lunch', label: 'Lunch', icon: Utensils },
  { value: 'dinner', label: 'Dinner', icon: ChefHat },
];

export const RegionsDrawer = ({ open, onClose, onSpinComplete }) => {
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeMeal, setActiveMeal] = useState('breakfast');
  const [cache, setCache] = useState({}); // {`${region}|${meal}`: items}

  useEffect(() => {
    if (!activeRegion) return;
    const key = `${activeRegion}|${activeMeal}`;
    if (cache[key]) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes`, {
          params: { region: activeRegion, category: activeMeal },
        });
        setCache((p) => ({ ...p, [key]: data }));
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('regions fetch fail', err);
      }
    })();
  }, [activeRegion, activeMeal, cache]);

  // Reset on close
  useEffect(() => { if (!open) { setActiveRegion(null); setActiveMeal('breakfast'); } }, [open]);

  const items = activeRegion ? cache[`${activeRegion}|${activeMeal}`] || [] : [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl p-0 bg-white border-none shadow-2xl overflow-hidden"
        data-testid="regions-drawer"
      >
        <div className="grid md:grid-cols-[260px,1fr] min-h-[460px]">
          {/* Left: region list */}
          <aside className="bg-[#FDFBF7] border-r border-[#1C1917]/10 p-5">
            <div className="mb-5">
              <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#1C1917]">Regions</h3>
            </div>
            <ul className="space-y-1">
              {REGIONS.map((r) => {
                const isActive = activeRegion === r.id;
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => setActiveRegion(r.id)}
                      data-testid={`regions-drawer-${r.id.toLowerCase()}`}
                      className={`w-full flex items-start gap-2 px-3 py-3 rounded-xl text-left transition-colors ${
                        isActive ? 'bg-white shadow-sm border border-[#F97316]/30' : 'hover:bg-white/70'
                      }`}
                    >
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: r.accent }} />
                      <div className="min-w-0">
                        <p className="font-['Playfair_Display'] font-bold text-[#1C1917] leading-tight">{r.id}</p>
                        <p className="text-[11px] text-[#1C1917]/55 font-['Outfit'] mt-0.5">{r.tagline}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 pt-5 border-t border-[#1C1917]/10">
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-3">
                <Sparkles className="w-3 h-3" /> Coming soon
              </p>
              <div className="flex flex-wrap gap-1.5">
                {COMING_SOON.map((c) => (
                  <span
                    key={c}
                    className="px-2.5 py-1 text-[11px] rounded-full bg-[#1C1917]/5 text-[#1C1917]/55 font-['Outfit']"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-[#1C1917]/40 italic font-['Outfit'] mt-3">
                More authentic regional kitchens are on the way.
              </p>
            </div>
          </aside>

          {/* Right: wheel */}
          <main className="p-6 md:p-8">
            {!activeRegion ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <MapPin className="w-12 h-12 text-[#F97316]/60 mb-4" />
                <h4 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-2">
                  Choose a region
                </h4>
                <p className="text-sm text-[#1C1917]/60 font-['Outfit'] max-w-sm">
                  Pick a state from the list, then choose a meal type to spin.
                </p>
              </div>
            ) : (
              <div data-testid={`regions-drawer-panel-${activeRegion.toLowerCase()}`}>
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-widest text-[#1C1917]/40 font-['Outfit'] mb-1">Region</p>
                  <h4 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917]">{activeRegion} Kitchen</h4>
                </div>

                <Tabs value={activeMeal} onValueChange={setActiveMeal} className="w-full">
                  <TabsList className="grid w-full max-w-md grid-cols-3 mb-5 bg-[#FDFBF7] rounded-full p-1">
                    {MEALS.map((m) => {
                      const Icon = m.icon;
                      return (
                        <TabsTrigger
                          key={m.value}
                          value={m.value}
                          data-testid={`regions-drawer-meal-${m.value}`}
                          className="rounded-full data-[state=active]:bg-[#F97316] data-[state=active]:text-white font-['Outfit'] font-medium"
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {m.label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {MEALS.map((m) => (
                    <TabsContent key={m.value} value={m.value}>
                      <div className="flex justify-center">
                        {items.length > 0 ? (
                          <SpinnerWheel
                            items={items}
                            onSpinComplete={(r) => { onSpinComplete(r); onClose(); }}
                            size={340}
                          />
                        ) : (
                          <p className="py-12 text-[#1C1917]/50 font-['Outfit']">Loading dishes…</p>
                        )}
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
};
